<template>
  <v-container id="create-extarnal-chain">
    <!-- header -->
    <v-row class="mb-6 px-6" justify="center" align="center">
      <v-btn
        id="cancel"
        class="elevation-0 pl-0 pr-3"
        color="black--text grey lighten-2"
        @click="$router.push('/signatureChains/list/ALL')"
      >
        <v-icon class="ma-0 pa-0" color="grey darken-3">
          mdi-chevron-left
        </v-icon>
        {{ $t("main.back") }}
      </v-btn>
      <v-col cols="7">
        <span id="filename-text" class="text-h5"> {{ document.fileName }}</span>
      </v-col>

      <v-col class="d-flex flex-row justify-end">
        <v-btn
          v-if="!!canSign"
          id="sign-doc-button"
          color="success"
          class="mx-4 px-4"
          :disabled="checkSignButtonDisabled"
          @click="createChain(true)"
        >
          <v-icon class="mr-1 ml-0">mdi-draw-pen</v-icon>
          {{ $t("main.sign") }}
        </v-btn>

        <v-btn
          v-else
          id="create-chain-button"
          class="mx-4 px-4"
          color="primary"
          :disabled="checkSignButtonDisabled"
          :loading="isUploading"
          @click="createChain(false)"
        >
          <v-icon class="mr-1 ml-0">mdi-plus-circle-outline</v-icon>
          {{ $t("documentSignatureCustomization.title.createChain") }}
        </v-btn>
      </v-col>
    </v-row>
    <v-row align="stretch" align-content="space-between">
      <!-- PARTICIPANTS -->
      <v-col sm="12" md="6" align-self="start">
        <v-card class="py-4 px-3">
          <v-switch
            id="ordered-switch"
            v-model="chosenSignatureType"
            inset
            class="mt-4"
            color="blue darken-2"
            :false-value="types.UNORDERED"
            :true-value="types.ORDERED"
            :label="
              chosenSignatureType === types.UNORDERED
                ? $t(
                  'documentSignatureCustomization.content.createChain.chainType.unorderedDescription'
                )
                : $t(
                  'documentSignatureCustomization.content.createChain.chainType.orderedDescription'
                )
            "
            prepend-icon="mdi-graph-outline"
            @click="_computedWatchers.canSign.run()"
          >
          </v-switch>
          <v-switch
            v-if="signingTrailEnabled && !asicMode"
            id="signingtrail_switch"
            v-model="useSigningTrail"
            color="blue darken-2"
            :label="useSigningTrail
              ? $t('documentSignatureCustomization.content.createChain.useSigningTrail.true')
              : $t('documentSignatureCustomization.content.createChain.useSigningTrail.false')"
            prepend-icon="mdi-file-certificate-outline"
            inset
          >
          </v-switch>
          <v-row v-if="signingTrailEnabled && useSigningTrail" align="center">
            <v-col cols="1" />
            <v-col>
              <v-radio-group v-model="attachedOrDetachedSigningTrail" class="pt-0 mt-0" row>
                <v-radio id="radio_attached" color="blue darken-2" :label="$t('documentSignatureCustomization.content.createChain.useSigningTrail.signingTrailMode.ATTACHED')" value="ATTACHED" />
                <v-radio id="radio_detached" color="blue darken-2" :label="$t('documentSignatureCustomization.content.createChain.useSigningTrail.signingTrailMode.DETACHED')" value="DETACHED" />
              </v-radio-group>
            </v-col>
          </v-row>
          <v-data-table
            id="virtual-scroll-table"
            ref="dtable"
            :headers="headers"
            :items="addedUsers"
            item-key="uniqueId"
            :items-per-page="-1"
            :no-data-text="$t('dataTable.content.noDataAvailable')"
            hide-default-footer
            dense
            class="mb-2"
          >
            <template #body="props">
              <draggable :list="addedUsers" tag="tbody" @change="_computedWatchers.canSign.run()">
                <tr v-for="(user, index) in props.items" :key="index">
                  <td>
                    <span>{{ getRank(user) }}</span>
                  </td>
                  <td>
                    <long-text :text="getFullName(user)"> </long-text>
                  </td>
                  <td>
                    <span class="caption">
                      &lt;<long-text :text="user.email" :max-char="29">
                      </long-text>&gt;
                    </span>
                  </td>
                  <td>
                    <v-tooltip bottom>
                      <template #activator="{ on, attrs }">
                        <v-btn
                          id="toggle_user_role"
                          v-bind="attrs"
                          icon
                          :disabled="
                            !!userGroupsEnabled &&
                              isUserDisabledForRoleChange(user)
                          "
                          v-on="on"
                          @click="user.role = getToggledUserRole(user)"
                        >
                          <v-icon>
                            {{ getToggledUserRoleIcon(user.role) }}
                          </v-icon>
                        </v-btn>
                      </template>
                      <span>{{ getRoleTooltipText(user.role) }}</span>
                    </v-tooltip>
                  </td>
                  <td align="right">
                    <v-row>
                      <v-spacer></v-spacer>

                      <!-- Menu for setting Subsigner -->
                      <v-btn
                        v-show="user.role === memberRole.CLERK" id="set_subsigner_button" text class="pa-0"
                        @click="showSubsignerMenu(user, index, $event['clientX'], $event['clientY']) "
                      >
                        <v-icon>mdi-account-supervisor</v-icon>
                        {{ user.subsignerName ? $t('dataTable.content.action.changeSubsigner') :
                          $t('dataTable.content.action.setSubsigner') }}
                      </v-btn>

                      <v-btn
                        text
                        color="error"
                        @click="removeUser(user, index)"
                      >
                        <v-icon>mdi-account-remove</v-icon>
                        {{ $t("dataTable.content.action.removeUser") }}
                      </v-btn>

                      <v-icon>mdi-drag-vertical</v-icon>
                    </v-row>
                  </td>
                </tr>
              </draggable>
            </template>
          </v-data-table>
          <v-autocomplete
            id="autocomplete_input"
            v-model="nextUserToAdd"
            :items="enabledUsers"
            item-text="email"
            return-object
            :label="
              $t(
                'documentSignatureCustomization.content.createChain.recipient'
              ) + `*`
            "
            :placeholder="$t('autocomplete.startTyping')"
            prepend-inner-icon="mdi-fountain-pen-tip"
            :no-data-text="noDatatext"
            clearable
            outlined
            hide-details
            dense
            :search-input="search"
            @update:search-input="listEnabledUsersWacther"
            @keydown.enter="addUser"
            @click.prevent="addUser"
          >
            <template slot="item" slot-scope="{ item }">
              <v-row align-content="center" @mouseup="addUser(item)">
                <span class="body-2 py-4">{{ item.firstName }} {{ item.lastName }}
                  <span v-if="!!item.email" class="caption">&lt;{{ item.email }}&gt;
                  </span></span>
                <v-spacer></v-spacer>
              </v-row>
            </template>
            <!-- Button to add user if no results found -->
            <template
              v-if="
                !userGroupsEnabled &&
                  !enabledUsers.length &&
                  displayInviteButton
              "
              #no-data
            >
              <v-row>
                <v-col class="ml-3">
                  <v-label class="ml-6">{{ noDatatext }}</v-label>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-btn
                    id="invite_user"
                    class="ml-3"
                    color="primary"
                    @click="addUserAsInvite()"
                  >
                    {{
                      $t(
                        "documentSignatureCustomization.content.createChain.iniviteUserButton"
                      )
                    }}
                  </v-btn>
                </v-col>
              </v-row>
            </template>
          </v-autocomplete>
          <v-btn
            id="add-me-button"
            color="transparent"
            tile
            class="pl-0 pr-1 mt-2 rounded-l-xl"
            :ripple="false"
            @click="addMe()"
          >
            <v-icon large class="modalButton"> mdi-plus-circle </v-icon>
            {{ $t("documentSignatureCustomization.content.createChain.addme") }}
          </v-btn>
        </v-card>
      </v-col>
      <!-- MENU -->
      <v-dialog v-model="subsignerNameMenuVisible" max-width="378">
        <v-card id="subsigner_card" class="pa-2">
          <v-card-subtitle class="subtitle-1">
            {{ $t('dataTable.content.action.setSubsignerDescription') }}
          </v-card-subtitle>
          <v-card-text>
            <v-form @submit.prevent="setSubsigner(currentEditedUserIndex)">
              <v-text-field
                id="subsigner_input" ref="subsignerInput" v-model="subsignerName" clearable
                dense hide-details :label="$t('dataTable.content.action.subsignerName')"
                @keyup.enter="setSubsigner(currentEditedUserIndex)"
              />
              <v-btn
                id="save_subsigner" class="mt-2" color="primary"
                @click="setSubsigner(currentEditedUserIndex)"
              >
                <v-icon>mdi-check-circle-outline</v-icon>
                {{ $t('main.save') }}
              </v-btn>
            </v-form>
          </v-card-text>
        </v-card>
      </v-dialog>
      <!--  PDF -->
      <v-col sm="12" md="6">
        <v-overlay :absolute="true" :value="isUploading">
          <v-progress-circular
            indeterminate
            size="64"
            color="primary"
          ></v-progress-circular>
        </v-overlay>
        <visibleSignature
          ref="visibleSignature"
          :doc-data="documentBase64"
          :sign-prof="undefined"
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import LongText from '@/components/LongText.vue'
import users from '@/services/api/users'
import debounce from 'lodash.debounce'
import Draggable from 'vuedraggable'
import VisibleSignature from '../../../../components/VisibleSignature'
import { showToastError, showToastSuccess } from '../../../../event-bus'
import documents from '../../../../services/api/documents'
import signatureChains from '../../../../services/api/signatureChains'
import memberRole from '../memberRole.js'
import SignatureChainStatus from '../SignatureChainStatus'
import SignatureChainType from '../SignatureChainType'

export default {
  components: {
    VisibleSignature,
    Draggable,
    LongText
  },
  data() {
    return {
      memberRole: memberRole,
      documentBase64: '',
      document: {},
      isUploading: true,
      enabledUsers: [],
      addedUsers: [],
      chosenSignatureType: 'UNORDERED',
      nextUserToAdd: {},
      useSigningTrail: true,
      signingTrailEnabled: this.$store.state.remoteConfig.signingTrailEnabled,
      attachedOrDetachedSigningTrail: 'ATTACHED',
      search: '',
      includesHimself: false,
      // canSign: false,
      asicMode: false,
      types: { ...SignatureChainType }, // only needed for template
      headers: [
        {
          text: this.$t('dataTable.header.users.rank'),
          sortable: false,
          align: 'left',
          value: 'rank',
          width: '5%'
        },
        {
          text: this.$t('dataTable.header.users.name'),
          align: 'left',
          value: 'name'
        },
        {
          text: this.$t('dataTable.header.users.email'),
          align: 'left',
          value: 'email'
        },
        {
          text: this.$t(
            'documentSignatureCustomization.content.createChain.role.header'
          ),
          align: 'left',
          value: 'role',
          width: '1%'
        },
        {
          text: this.$t('dataTable.header.actions'),
          align: 'right',
          value: 'actions',
          sortable: false
        }
      ],
      noDatatext: this.$t('autocomplete.startTyping'),
      userGroupsEnabled: this.$store.state.remoteConfig
        ? this.$store.state.remoteConfig.userGroupsEnabled
        : false,
      displayInviteButton: false,
      subsignerName: '',
      enabledUsersFixed: [],
      currentEditedUserIndex: null,
      subsignerNameMenuVisible: false,
      menuX: 0,
      menuY: 0
    }
  },

  computed: {
    currentUser() {
      return this.$store.state.currentUser
    },
    checkSignButtonDisabled() {
      const d =
        this.addedUsers.length === 0 ||
        this.addedUsers.every((x) => x.role === memberRole.VIEWER)
      return d
    },
    clerkEnabled() {
      return this.$store.state.remoteConfig.clerkEnabled
      // return true
    },
    canSign() {
      const hasAddedUsers = this.addedUsers.length > 0
      const isNotBulkUpload = !this.bulkUploadMode
      const isCurrentUserFirst =
        hasAddedUsers &&
        this.addedUsers[0].id === this.$store.state.currentUser.id
      const isUnorderedWithHimself =
        this.chosenSignatureType === SignatureChainType.UNORDERED && this.includesHimself

      return hasAddedUsers && isNotBulkUpload && (isCurrentUserFirst || isUnorderedWithHimself)
    },
    /* canSignCalc() {
      const hasAddedUsers = this.addedUsers.length > 0
      const isCurrentUserFirst =
    hasAddedUsers &&
    this.addedUsers[0].id === this.$store.state.currentUser.id
      const isUnorderedWithHimself = this.chosenSignatureType === signatureChainType.UNORDERED && this.includesHimself

      this.canSign = hasAddedUsers && (isCurrentUserFirst || isUnorderedWithHimself)
    }, */
    isApproveMode() {
      const isCurrentUserFirstApprover =
        (this.addedUsers[0].role === memberRole.APPROVER) && this.chosenSignatureType === SignatureChainType.ORDERED

      const isUnorderedCurrentUserApprover =
        this.chosenSignatureType === SignatureChainType.UNORDERED &&
        this.includesHimself &&
        this.addedUsers.some(user => user.id === this.currentUser.id && user.role === memberRole.APPROVER)

      return isCurrentUserFirstApprover || isUnorderedCurrentUserApprover
    },

    isClerkMode() {
      const isCurrentUserFirstClerk =
        (this.addedUsers[0].role === memberRole.CLERK) && this.chosenSignatureType === SignatureChainType.ORDERED

      const isUnorderedCurrentUserClerk =
        this.chosenSignatureType === SignatureChainType.UNORDERED &&
        this.includesHimself &&
        this.addedUsers.some(user => user.id === this.currentUser.id && user.role === memberRole.CLERK)

      return isCurrentUserFirstClerk || isUnorderedCurrentUserClerk
    }
  },
  async mounted() {
    await this.getDocument()
    await this.listEnabledUsers()
    const pageReq = {
      maxResults: 100
    }
    const resp = await users.listEnabledUsers('', pageReq)
    this.enabledUsersFixed = resp.items
  },
  methods: {
    async getDocument() {
      this.isUploading = true
      try {
        const blob = await documents.downloadDocument(this.$route.params.docId)
        const base64 = await this.blobToBase64(blob)
        const docuDto = await documents.getDocument(this.$route.params.docId)
        this.document = docuDto
        this.documentBase64 = base64.split(',')[1]
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      } finally {
        this.isUploading = false
      }
    },
    blobToBase64(blob) {
      return new Promise((resolve) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result)
        reader.readAsDataURL(blob)
      })
    },

    listEnabledUsersWacther: debounce(function(search) {
      this.listEnabledUsers(search)
    }, 500),
    async listEnabledUsers(searchUser) {
      const pageReq = {
        maxResults: 5
      }
      let searchUserlen = 0
      try {
        searchUserlen = searchUser.length
      } catch (err) {}
      if (searchUserlen > 0) {
        try {
          const resp = await users.listEnabledUsers(searchUser, pageReq)
          this.enabledUsers = resp.items

          if (resp.items.length === 0) {
            this.noDatatext = this.$t(
              'documentSignatureCustomization.content.createChain.userNotFound'
            )

            if (this.isValidEmail(searchUser)) {
              this.invitableUserEmail = searchUser
              this.displayInviteButton = true
            } else {
              this.displayInviteButton = false
            }
          } else {
            this.displayInviteButton = false
          }
        } catch (error) {
          const errorMsg = this.getErrorMessage(error)
          showToastError(errorMsg)
        }
      } else {
        this.noDatatext = this.$t('autocomplete.startTyping')
      }
    },
    canSignCalc() {
      const hasAddedUsers = this.addedUsers.length > 0
      const isCurrentUserFirst =
    hasAddedUsers &&
    this.addedUsers[0].id === this.$store.state.currentUser.id
      const isUnorderedWithHimself = this.chosenSignatureType === SignatureChainType.UNORDERED && this.includesHimself

      this.canSign = hasAddedUsers && (isCurrentUserFirst || isUnorderedWithHimself)
    },
    addUser(user) {
      if (user.id === this.currentUser.id) {
        if (this.currentUser.roles.includes('signer')) {
          user.role = memberRole.CREATOR_AND_SIGNER
        } else {
          showToastError(this.$t('documentSignatureCustomization.warning.creatorCantAddAsViewer'))
          return
        }
      } else {
        if (user.roles && user.roles.includes('signer')) {
          user.role = memberRole.SIGNER
        } else {
          user.role = memberRole.VIEWER
        }
      }

      /* if (user.id === this.currentUser.id) {
        user.role = memberRole.CREATOR_AND_SIGNER
      } else {
        user.role = memberRole.SIGNER
      } */

      this.nextUserToAdd = user

      // we need to copy the object and assign a unique id for it to avoud Vue duplicate key warnings
      this.nextUserToAdd = Object.assign({}, this.nextUserToAdd)
      this.nextUserToAdd.uniqueId = this.addedUsers.length + 1
      // explanation: somehow a empty array is not an array: error: .include is  not a function
      if (this.nextUserToAdd !== undefined && this.nextUserToAdd.email) {
        this.addedUsers.push(this.nextUserToAdd)
        // this.$set(this.addedUsers, this.addedUsers.length - 1, this.nextUserToAdd)
        if (this.nextUserToAdd.id === this.currentUser.id) {
          this.includesHimself = true
        }
        this.nextUserToAdd = ''
        this.search = ''
      }
      const container = this.$refs.dtable.$el
      container.scrollTop = container.scrollHeight - 1

      // this.canSignCalc()
    },
    addMe() {
      this.addUser(this.currentUser)
    },
    getRank(user) {
      if (
        this.chosenSignatureType === SignatureChainType.UNORDERED ||
        user.role === memberRole.VIEWER
      ) {
        return '•'
      } else {
        const signers = this.addedUsers.filter(
          (x) => x.role !== memberRole.VIEWER
        )
        const index = signers.findIndex((x) => x.uniqueId === user.uniqueId)

        return index + 1
      }
    },
    getFullName(user) {
      if (user.firstName && user.lastName) {
        return `${user.firstName} ${user.lastName}`
      } else if (user.firstName) {
        return `${user.firstName}`
      } else if (user.lastName) {
        return `${user.lastName}`
      } else return ''
    },
    getToggledUserRole(user) {
      const { role } = user

      const userHaveRoles = user.roles
      const userHaveApprover = userHaveRoles && user.roles.includes('approver')
      const userHaveSign = userHaveRoles && user.roles.includes('signer')
      const canAddApprover = userHaveApprover
      const canAddSigner = !userHaveRoles || userHaveSign
      const canAddViewer = userHaveRoles
      const canAddClerk = this.clerkEnabled && (!userHaveRoles || userHaveSign)
      if (user.id === this.currentUser.id) {
        switch (role) {
          case memberRole.CREATOR_AND_SIGNER:
            return this.clerkEnabled ? memberRole.CLERK : (canAddApprover ? memberRole.APPROVER : memberRole.VIEWER)
          case memberRole.CLERK:
            return this.clerkEnabled ? (canAddApprover ? memberRole.APPROVER : memberRole.VIEWER) : memberRole.VIEWER
          case memberRole.VIEWER:
            return canAddApprover ? memberRole.APPROVER : memberRole.CREATOR_AND_SIGNER
          case memberRole.APPROVER:
            return memberRole.CREATOR_AND_SIGNER
          default:
            return role
        }
      } else {
        const rolesOrder = [
          this.memberRole.SIGNER,
          this.memberRole.CLERK,
          this.memberRole.VIEWER,
          this.memberRole.APPROVER
        ]
        const canAddMap = {
          [this.memberRole.SIGNER]: canAddSigner,
          [this.memberRole.CLERK]: canAddClerk,
          [this.memberRole.VIEWER]: canAddViewer,
          [this.memberRole.APPROVER]: canAddApprover
        }

        const currentIndex = rolesOrder.indexOf(role)
        const totalRoles = rolesOrder.length

        for (let i = 1; i <= totalRoles; i++) {
          const nextIndex = (currentIndex + i) % totalRoles
          const nextRole = rolesOrder[nextIndex]

          if (canAddMap[nextRole]) {
            return nextRole
          }
        }
        console.error('Unknown role for non-current user', role)
        return role
      }
    },
    getToggledUserRoleIcon(role) {
      switch (role) {
        case memberRole.CREATOR:
          return 'mdi-file-sign'
        case memberRole.SIGNER:
          return 'mdi-file-sign'
        case memberRole.CREATOR_AND_SIGNER:
          return 'mdi-file-sign'
        case memberRole.VIEWER:
          return 'mdi-eye-outline'
        case memberRole.CLERK:
          return 'mdi-account-tie'
        case memberRole.APPROVER:
          return 'mdi-check-decagram'
        default:
          console.error('Unknown role', role)
      }
    },

    getRoleTooltipText(role) {
      switch (role) {
        case memberRole.CREATOR_AND_SIGNER:
          return this.$t('documentSignatureCustomization.content.createChain.role.creatorExplainer')
        case memberRole.SIGNER:
          return this.$t('documentSignatureCustomization.content.createChain.role.signerExplainer')
        case memberRole.VIEWER:
          return this.$t('documentSignatureCustomization.content.createChain.role.viewerExplainer')
        case memberRole.CLERK:
          return this.$t('documentSignatureCustomization.content.createChain.role.clerkExplainer')
        case memberRole.APPROVER:
          return this.$t('signatureChains.states.approverExplainer')
        default:
          console.error('Unknown role', role)
          break
      }
    },
    removeUser(user, index) {
      if (user.id === this.currentUser.id) {
        this.includesHimself = false
      }
      this.addedUsers.splice(index, 1)
      // this.canSignCalc()
    },
    async createChain(sign = false) {
      const viewerEmails = this.addedUsers.filter(user => user.role === memberRole.VIEWER || user.role === memberRole.CREATOR).map(x => x.email)

      const req = {
        type: this.chosenSignatureType,
        origDocId: this.$route.params.docId,
        signers: [],
        viewerEmails: viewerEmails,
        externalId: this.$route.params.externalId,
        externalCallbackUrl: this.$route.query.callbackUrl
      }
      if (this.signingTrailEnabled) {
        req.signingTrailMode = this.useSigningTrail ? this.attachedOrDetachedSigningTrail : 'NONE'
      }
      /* const signerEmails = this.addedUsers
        .filter(
          (user) =>
            user.role === memberRole.SIGNER ||
            user.role === memberRole.CREATOR_AND_SIGNER
        )
        .map((x) => x.email)
      const clerks = this.addedUsers.filter(
        (user) => user.role === memberRole.CLERK
      ) */

      /* if (this.clerkEnabled) {
        clerks.forEach((x) => {
          req.signers.push({
            email: x.email,
            clerkMode: true,
            subsignerName: x.subsignerName
          })
        })
      }
      signerEmails.forEach((x) => {
        req.signers.push({ email: x, clerkMode: false })
      }) */

      this.addedUsers.forEach(user => {
        if (user.role === memberRole.VIEWER) {
          return
        }
        const addableSignature = {
          email: user.email,
          clerkMode: this.clerkEnabled && (user.role === memberRole.CLERK),
          approver: user.role === memberRole.APPROVER
        }
        if (this.clerkEnabled && (user.role === memberRole.CLERK)) {
          addableSignature.subsignerName = user.subsignerName
        }

        req.signers.push(addableSignature)
      })

      this.isUploading = true
      try {
        const resp = await signatureChains.createSignatureChain(req)
        if (sign) {
          if (this.canSign && this.isClerkMode) {
            this.$store.commit('setCurrentChain', resp)
            this.$router.push('/document/' + resp.origDocument.id + '/clerk/' + this.getUserSignatureIdForClerk(resp) + '/' + this.getCurrentSignatureId(resp))

            return
          }
          if (this.canSign && this.isApproveMode) {
            this.$router.push('/document/' + resp.origDocument.id + '/approve-customization/' + this.getUserSignatureIdForApprover(resp) + '/' + this.getCurrentSignatureId(resp))

            return
          }
          this.$router.push('/document/' + resp.id + '/' + resp.origDocument.id + '/customization/signature/' + this.getUserSignatureId(resp) + this.getCurrentSignatureId(resp))
        } else {
          showToastSuccess(this.$t('toastMessage.succesfullyCreated'))
          this.$router.push({ name: 'SignatureChains' })
        }
      } catch (error) {
        console.error(error)
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      } finally {
        this.isUploading = false
      }
    },
    getUserSignatureId(item) {
      const userSignature = item.signatures.find(
        (signature) =>
          signature.user.id === this.currentUser.id &&
          signature.status === SignatureChainStatus.CREATED
      )
      return userSignature.id
    },
    getUserSignatureIdForApprover(item) {
      const userSignature = item.signatures.find(signature => (signature.user.id === this.currentUser.id && signature.status === SignatureChainStatus.CREATED && signature.approver === true))
      return userSignature.id
    },
    getUserSignatureIdForClerk(item) {
      const userSignature = item.signatures.find(signature => (signature.user.id === this.currentUser.id && signature.status === SignatureChainStatus.CREATED && signature.clerkMode === true))
      return userSignature.id
    },
    getCurrentSignatureId(item) {
      let currentSignatureId = '/'
      if (item.type === SignatureChainType.ORDERED) {
        currentSignatureId = ''
      } else {
        if (item.currentSignatureId === undefined) {
          currentSignatureId = ''
        } else {
          currentSignatureId += item.currentSignatureId
        }
        // currentSignatureId += item.signatures.findIndex(signature => signature.user.id === this.currentUser.id)
      }

      return currentSignatureId
    },
    setSubsigner(index) {
      /* if (this.subsignerName?.trim()) {
        this.addedUsers[index].subsignerName = this.subsignerName
        this.subsignerNameMenuVisible = false // Close the menu
      } else {
      } */
      // this.addedUsers[index].subsignerName = this.subsignerName
      this.$set(this.addedUsers[index], 'subsignerName', this.subsignerName)
      this.subsignerNameMenuVisible = false // Close the menu
    },
    isValidEmail(email) {
      // Regular expression for validating an email address
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(email)
    },
    addUserAsInvite() {
      const invitableUser = {}
      invitableUser.roles = []
      invitableUser.roles.push('signer')
      invitableUser.email = this.invitableUserEmail
      this.addUser(invitableUser)
    },

    isUserDisabledForRoleChange(user) {
      const isEmailInArray = this.enabledUsersFixed.some(item => item.email === user.email)
      return !isEmailInArray || (user.roles && !user.roles.includes('signer'))
    },
    showSubsignerMenu(item, index, x, y) {
      if (item.subsignerName) { this.subsignerName = item.subsignerName }

      this.currentEditedUserIndex = index
      this.menuX = x
      this.menuY = y
      this.subsignerNameMenuVisible = true
    }
  }
}
</script>

<style scoped>
#virtual-scroll-table {
  max-height: 20vh;
  overflow-y: auto;
  overflow-x: hidden !important;
}
</style>
